import { FC } from 'react';
import {
  makeStyles,
  tokens,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Button,
  Subtitle2,
  Subtitle1,
} from '@fluentui/react-components';
import { WarningRegular } from '@fluentui/react-icons';
import { HEADER_CELLS } from '../complianceDashboard.constants';
import { SmartTableCell } from '@shared/components';
import { TItemWithIssues } from '../types';

/**
 * Example styles using Fluent UI's makeStyles + Tailwind for layout.
 */
const useStyles = makeStyles({
  tableContainer: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingHorizontalL,
    boxShadow: tokens.shadow2,
    border: `1px solid ${tokens.colorBrandStroke2}`,
  },
  badgeGroup: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
  tableRow: {
    borderBottom: 'none',
  },
});

type TProps = {
  items: TItemWithIssues[];
};

const ComplianceIssuesTable: FC<TProps> = ({ items }) => {
  const classes = useStyles();

  return (
    <div className="flex flex-col gap-4 pb-8">
      <div className="flex justify-between w-full">
        <div className="flex items-center gap-2">
          <WarningRegular fontSize="24px" color="red" />
          <Subtitle1>7 files with compliance issues</Subtitle1>
        </div>
        <Button appearance="primary" onClick={() => alert('Coming soon...')}>
          Filter &amp; Sort
        </Button>
      </div>

      <div className={classes.tableContainer}>
        <Table aria-label="Files with compliance issues" size="medium">
          <TableHeader>
            <TableRow>
              {HEADER_CELLS.map((cell, idx) => (
                <TableHeaderCell key={idx} style={{ width: cell.width }}>
                  <div className={`${cell.isCentralized ? '!mx-auto w-fit' : ''}`}>
                    <Subtitle2>{cell.title}</Subtitle2>
                  </div>
                </TableHeaderCell>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((file, idx) => (
              <TableRow key={idx} className={classes.tableRow}>
                <SmartTableCell variant={{ kind: 'item', item: file }} />
                <TableCell>
                  <div className="!mx-auto w-fit">
                    <Badge appearance="tint" shape="rounded" color="danger">
                      {file.unresolvedIssues}
                    </Badge>
                  </div>
                </TableCell>
                <TableCell>
                  <div className={classes.badgeGroup}>
                    {file.errorCategories.map((category, cIdx) => (
                      <Badge key={cIdx} appearance="tint" shape="rounded">
                        {category}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="!mx-auto w-fit">{file.latestVersion}</div>
                </TableCell>
                <TableCell>
                  <div className="!mx-auto w-fit">{file.approvedVersion}</div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default ComplianceIssuesTable;
